// Threads a per-callsite logger (e.g. `logger(import.meta.location)`) from a drizzle
// query object down into the prepared query that actually runs it, without
// patching drizzle-orm.
//
// Why a module-scoped slot rather than a parameter or AsyncLocalStorage:
//
//   - drizzle's `QueryPromise.then`/`execute` and `SQLiteRelationalQuery._prepare`
//     are runtime classes we don't own, so we can't pass the logger through them.
//   - The window between "query starts executing" and "our `prepareQuery`
//     constructs an `ExpoSQLiteAsyncPreparedQuery`" is fully SYNCHRONOUS
//     (`then` → `execute` → `_prepare` → `session.prepareQuery` → `new
//     ExpoSQLiteAsyncPreparedQuery(...)`). A plain variable bridges it safely —
//     there is no `await` in between to interleave another query.
//   - AsyncLocalStorage isn't available in the RN/Hermes runtime, and the same
//     query object may be re-run on every change-listener event, so the logger
//     must live on the query object (`CALLSITE_LOGGER`) and be re-pushed each run.

/** Non-enumerable property key stamped onto a built drizzle query object. */
export const CALLSITE_LOGGER = Symbol.for("han.drizzle.callsiteLogger");

/** Marks a query object whose `then`/`execute` are already logger-wrapped. */
export const LOGGER_PROXIED = Symbol.for("han.drizzle.loggerProxied");

// Property key on the raw connection that `create-performance-logger.ts` reads
// to suppress its own per-query log. The vendored prepared query bumps this
// while it runs a query it is itself logging via the callsite logger, so a
// single drizzle query produces exactly one log line (with the call site).
export const PREPARED_LOG_SUPPRESSION_DEPTH =
  "__haniverseDatabaseLoggerPreparedLogSuppressionDepth";

// Property key on the raw connection naming which logical database it is —
// `"actorDb"` / `"contentDb"` / `"staticDb"`. Stamped once at open time by
// `create-performance-logger.ts` and read by BOTH logging layers (the
// raw-connection wrapper and the vendored prepared query) so every query line is
// prefixed with its source database.
export const DATABASE_NAME_TAG = "__haniverseDatabaseNameTag";

/** Read the logical database name stamped on a connection, or null if none. */
export function getDatabaseNameTag(connection: object): string | null {
  const tag = Reflect.get(connection, DATABASE_NAME_TAG);
  return typeof tag === "string" ? tag : null;
}

/** Stamp the logical database name onto a connection (idempotent). */
export function setDatabaseNameTag(connection: object, name: string): void {
  Reflect.set(connection, DATABASE_NAME_TAG, name);
}

/** Current raw-connection log suppression depth (0 when not suppressed). */
export function getSuppressionDepth(connection: object): number {
  const depth = Reflect.get(connection, PREPARED_LOG_SUPPRESSION_DEPTH);
  return typeof depth === "number" ? depth : 0;
}

/** Increment the raw-connection log suppression depth (re-entrant safe). */
export function incrementSuppressionDepth(connection: object): void {
  Reflect.set(connection, PREPARED_LOG_SUPPRESSION_DEPTH, getSuppressionDepth(connection) + 1);
}

/** Decrement the raw-connection log suppression depth, clearing it at zero. */
export function decrementSuppressionDepth(connection: object): void {
  const nextDepth = Math.max(0, getSuppressionDepth(connection) - 1);
  if (nextDepth === 0) {
    Reflect.deleteProperty(connection, PREPARED_LOG_SUPPRESSION_DEPTH);
    return;
  }
  Reflect.set(connection, PREPARED_LOG_SUPPRESSION_DEPTH, nextDepth);
}

let currentCallsiteLogger: Console | undefined;

/** A logger is the callable `Console`-shaped object produced by `logger(import.meta.location)`. */
export function isCallsiteLogger(value: unknown): value is Console {
  return (
    (typeof value === "object" || typeof value === "function") &&
    value !== null &&
    typeof Reflect.get(value, "debug") === "function"
  );
}

/** Read the callsite logger off a query object stamped via {@link CALLSITE_LOGGER}. */
export function getStampedCallsiteLogger(target: unknown): Console | undefined {
  if (typeof target !== "object" && typeof target !== "function") {
    return undefined;
  }
  if (target === null) {
    return undefined;
  }
  const logger = Reflect.get(target, CALLSITE_LOGGER);
  return isCallsiteLogger(logger) ? logger : undefined;
}

/** Push the logger for the synchronous prepared-query construction that follows. */
export function setCurrentCallsiteLogger(logger: Console | undefined): void {
  currentCallsiteLogger = logger;
}

/** Read and clear the pushed logger. Safe to call when none is set. */
export function takeCurrentCallsiteLogger(): Console | undefined {
  const logger = currentCallsiteLogger;
  currentCallsiteLogger = undefined;
  return logger;
}
